// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useState } from "react";
import { Segment } from "semantic-ui-react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import _ from "lodash";

import AFPlot from "../../../Lumber/Plotting/AFPlotly";
import { BOX_LOWER_FILL_COLOR, BOX_UPPER_FILL_COLOR, TRANSPARENT_COLOR } from "../../../../utilities/graphics";

const ProtocolBoxWhiskerChart = ({
	data,
	tukeyData,
	tukeyRunning,
	assessmentName,
	trialName,
	axisYTitle,
	chartHeight,
	allTreatmentData,
	needToRerunStats,
	needToRerunNormalization,
	initialGroupTrialDataCheckedCount,
	isCompiled = undefined,
	allTreatmentsUnchecked = false,
	hideUncheckedTrialData = false,
	disableColors = false,
	hideMeanComparisonResults = false,
	chartType = "BoxWhisker",
}) => {
	const colors = !disableColors
		? [
				"#920000",
				"#006DDB",
				"#B66DFF",
				"#24FF24",
				"#FFFF6D",
				"#004949",
				"#009292",
				"#FF6DB6",
				"#FFB6DB",
				"#490092",
				"#924900",
				"#DB6D00",
				"#6DB6FF",
				"#B6DBFF",
				"#000000",
			]
		: ["#920000"];
	let colorIdx = 0;

	const [chartOptions, setChartOptions] = useState({ data: [], layout: {} });
	const [chartLoading, setChartLoading] = useState(true);

	const protocolName = useSelector((state) => state.entityNames.protocolName ?? "");

	useEffect(() => {
		if (data?.length > 0 && (tukeyData?.length > 0 || tukeyData === undefined)) {
			stageChartOptions();
		} else {
			stageChartOptionsCallback();
		}
	}, [data, tukeyData, disableColors]);

	const stageChartOptionsCallback = useCallback(
		_.throttle(() => {
			if (data?.length > 0 && (tukeyData?.length > 0 || tukeyData === undefined)) {
				stageChartOptions();
			} else if (data?.length === 0) {
				setChartLoading(false);
			}
		}, 500),
		[data, tukeyData, disableColors],
	);

	const getNextMarker = () => {
		const marker = { color: colors[colorIdx], shape: "circle" };
		colorIdx = colorIdx + 1 >= colors.length ? 0 : colorIdx + 1;
		return marker;
	};

	const getSnapshotTrialName = () => {
		if (trialName) return trialName;

		const trialNames = _.uniq(_.compact(_.map(data, "trialName")));
		return trialNames.length === 1 ? trialNames[0] : protocolName;
	};

	const getSnapshotAssessmentName = () => {
		const snapshotAssessmentName = assessmentName ?? axisYTitle;
		return /\(([^)]+)\)/.exec(snapshotAssessmentName)?.[1] ?? snapshotAssessmentName;
	};

	const snapshotFileName = [getSnapshotTrialName(), getSnapshotAssessmentName(), chartType].filter(Boolean).join("_");

	function stageChartOptions() {
		let unGroupedScatterPlots = [];
		let isAnyTreatmentDeselected = false;
		let tempInitialGroupTrialDataCheckedCount = initialGroupTrialDataCheckedCount ?? [];

		let groupedTreatments = _.groupBy(data, "trialTreatmentId");
		let uniqueGroupedTreatments = _.uniqBy(data, "trialTreatmentId");
		let uniqueTreatmentsList = _.map(uniqueGroupedTreatments, (ugt) => ugt.trialTreatmentId);
		let uniqueGroupedTukeyTreatments = _.uniqBy(tukeyData, "trialTreatmentId");
		let tukeyDataList = _.map(uniqueGroupedTukeyTreatments, (ugt) => ugt.trialTreatmentId);
		let uniqueAllGroupedTreatments = allTreatmentData
			? _.uniqBy(allTreatmentData, "trialTreatmentId")
			: uniqueGroupedTreatments;
		let checkedTreatmentCountUpdated = _.map(uniqueAllGroupedTreatments, (d) => d.treatmentChecked);

		let treatmentIds = [];
		_.filter(tempInitialGroupTrialDataCheckedCount, (item, index) => {
			if (!item) {
				treatmentIds.push(index + 1);
			}
		});

		if (
			!_.isEqual(tempInitialGroupTrialDataCheckedCount, checkedTreatmentCountUpdated) &&
			tempInitialGroupTrialDataCheckedCount?.length !== 0
		) {
			isAnyTreatmentDeselected = true;
		} else if (initialGroupTrialDataCheckedCount?.length === 0) {
			if (
				_.some(uniqueGroupedTreatments, (d) => !d.treatmentChecked) ||
				_.some(checkedTreatmentCountUpdated, (ctcu) => ctcu === false)
			) {
				isAnyTreatmentDeselected = true;
			} else {
				isAnyTreatmentDeselected = false;
			}
		} else {
			isAnyTreatmentDeselected = false;
		}

		let boxX = [];
		let boxMin = [],
			boxQ1 = [],
			boxMedian = [],
			boxQ3 = [],
			boxMax = [];

		_.map(uniqueAllGroupedTreatments, (utr) => {
			const cat = String(utr.trialTreatmentId);
			const shouldRender =
				utr.treatmentChecked &&
				(_.isEmpty(tukeyDataList)
					? uniqueTreatmentsList.includes(utr.trialTreatmentId)
					: tukeyDataList.includes(utr.trialTreatmentId)) &&
				!needToRerunNormalization &&
				!allTreatmentsUnchecked &&
				!treatmentIds.includes(utr.trialTreatmentId) &&
				(_.some(utr.plots, (p) => p.plotId) || utr.plotName);

			boxX.push(cat);

			if (shouldRender) {
				const areas = _.map(groupedTreatments[utr.trialTreatmentId], "average");

				_.map(groupedTreatments[utr.trialTreatmentId], (treatment) => {
					if (isCompiled) {
						unGroupedScatterPlots.push({
							x: cat,
							y: treatment.average,
							plotName: treatment.plotName ?? _.map(treatment.plots, "plotName").join(", "),
							trialName: treatment.trialName,
							trialId: treatment.trialId,
							city: treatment.city,
							region: treatment.region,
							countryCode: treatment.countryCode,
							cooperatorName: treatment.cooperatorName ?? "Unknown Cooperator",
							rep: treatment.plotReplicate ?? 0,
						});
					} else {
						unGroupedScatterPlots.push({
							x: cat,
							y: treatment.average,
							plotName: treatment.plotName,
							rep: treatment.plotReplicate,
						});
					}
				});

				if (areas.length > 0) {
					const median = Number(medianBy([...areas]).toFixed(5));
					const lowerValues = getValuesAboveOrBelowMedian([...areas], median, false);
					const higherValues = getValuesAboveOrBelowMedian([...areas], median, true);
					boxMin.push(Number(Math.min(...areas).toFixed(5)));
					boxQ1.push(Number(medianBy(lowerValues).toFixed(5)));
					boxMedian.push(median);
					boxQ3.push(Number(medianBy(higherValues).toFixed(5)));
					boxMax.push(Number(Math.max(...areas).toFixed(5)));
				} else {
					boxMin.push(NaN);
					boxQ1.push(NaN);
					boxMedian.push(NaN);
					boxQ3.push(NaN);
					boxMax.push(NaN);
				}
			} else {
				boxMin.push(NaN);
				boxQ1.push(NaN);
				boxMedian.push(NaN);
				boxQ3.push(NaN);
				boxMax.push(NaN);
			}
		});

		const scatterPlots = isCompiled
			? _.groupBy(unGroupedScatterPlots, "trialId")
			: _.groupBy(unGroupedScatterPlots, "rep");

		const statsMap = {};
		boxX.forEach((cat, i) => {
			statsMap[cat] = { min: boxMin[i], q1: boxQ1[i], median: boxMedian[i], q3: boxQ3[i], max: boxMax[i] };
		});

		// Tukey letter annotations
		const showLetters =
			!needToRerunStats &&
			!needToRerunNormalization &&
			!hideUncheckedTrialData &&
			!allTreatmentsUnchecked &&
			!hideMeanComparisonResults;

		let letterAnnotations = [];
		if (tukeyData?.length > 0 && !tukeyRunning) {
			letterAnnotations = _.map(uniqueAllGroupedTreatments, (utr, idx) => {
				const treatmentId = utr.trialTreatmentId;
				const tukeyEntry = _.find(tukeyData, (td) => td.trialTreatmentId === treatmentId);
				const hideThisLetter =
					!uniqueTreatmentsList.includes(treatmentId) || !utr.treatmentChecked || isAnyTreatmentDeselected;
				const label = showLetters && tukeyEntry && !hideThisLetter ? (tukeyEntry.tukeyLetters ?? "") : "";
				return { x: idx, y: 1.06, yref: "paper", text: label, showarrow: false, font: { size: 14 }, textangle: -45 };
			});

			const allA = showLetters && _.every(letterAnnotations, (ann) => ann.text === "a");
			if (allA) {
				letterAnnotations.forEach((ann) => {
					if (ann.text !== "") ann.text = "-";
				});
			}
		}

		const numTreatments = letterAnnotations.length;
		const maxLabelLen = Math.max(1, ...letterAnnotations.map((a) => (a.text ?? "").trim().length));
		const tooMany = maxLabelLen * numTreatments > 140;
		if (tooMany) {
			letterAnnotations.forEach((a) => {
				a.text = "-";
			});
		}
		const annotationY = tooMany ? 1.15 : 1.15 + Math.max(0, (maxLabelLen - 2) * 0.015);
		const marginTop = tooMany ? 90 : 90 + Math.max(0, (maxLabelLen - 2) * 10);
		letterAnnotations.forEach((a) => {
			a.y = annotationY;
		});

		// Box is never thinner than a scatter marker (8px) at minimum expected chart width (600px)
		const scatterDataWidth = (8 / 600) * boxX.length;
		const boxWidth = Math.min(0.6, Math.max(0.2, scatterDataWidth));

		const boxBaseProps = {
			type: "box",
			x: boxX,
			boxpoints: false,
			showlegend: false,
			width: boxWidth,
		};

		const traces = [
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxQ1,
				q3: boxMedian,
				upperfence: boxMedian,
				fillcolor: BOX_LOWER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMedian,
				q1: boxMedian,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: BOX_UPPER_FILL_COLOR,
				line: { color: TRANSPARENT_COLOR, width: 0 },
				hoverinfo: "skip",
			},
			{
				...boxBaseProps,
				lowerfence: boxMin,
				q1: boxQ1,
				median: boxMedian,
				q3: boxQ3,
				upperfence: boxMax,
				fillcolor: TRANSPARENT_COLOR,
				line: { color: "black" },
				hoverinfo: "skip",
			},
		];

		for (const [key, sp] of Object.entries(scatterPlots)) {
			const marker = getNextMarker();
			const hovertemplate =
				isCompiled === "averages"
					? [
							"<b>%{customdata[0]}</b>",
							"<b>%{customdata[1]}, %{customdata[2]}, %{customdata[3]}</b>",
							"<b>Treatment: </b>%{customdata[4]}",
							"<b>Avg. Value: </b>%{y}",
							"",
							"<b>Min: </b>%{customdata[5]}",
							"<b>Q1: </b>%{customdata[6]}",
							"<b>Median: </b>%{customdata[7]}",
							"<b>Q3: </b>%{customdata[8]}",
							"<b>Max: </b>%{customdata[9]}",
							"<extra></extra>",
						].join("<br>")
					: isCompiled === "allReps"
						? [
								"<b>%{customdata[0]}</b>",
								"<b>%{customdata[1]}, %{customdata[2]}, %{customdata[3]}</b>",
								"<b>Plot: </b>%{customdata[4]}",
								"<b>Treatment: </b>%{customdata[5]}",
								"<b>Replicate: </b>%{customdata[6]}",
								"<b>Value: </b>%{y}",
								"",
								"<b>Min: </b>%{customdata[7]}",
								"<b>Q1: </b>%{customdata[8]}",
								"<b>Median: </b>%{customdata[9]}",
								"<b>Q3: </b>%{customdata[10]}",
								"<b>Max: </b>%{customdata[11]}",
								"<extra></extra>",
							].join("<br>")
						: [
								"<b>Plot: </b>%{customdata[0]}",
								"<b>Treatment: </b>%{customdata[1]}",
								"<b>Replicate: </b>%{customdata[2]}",
								"<b>Value: </b>%{y}",
								"",
								"<b>Min: </b>%{customdata[3]}",
								"<b>Q1: </b>%{customdata[4]}",
								"<b>Median: </b>%{customdata[5]}",
								"<b>Q3: </b>%{customdata[6]}",
								"<b>Max: </b>%{customdata[7]}",
								"<extra></extra>",
							].join("<br>");

			traces.push({
				type: "scatter",
				mode: "markers",
				name: isCompiled ? (sp[0].cooperatorName ?? key) : key,
				x: sp.map((p) => p.x),
				y: sp.map((p) => p.y),
				customdata: sp.map((p) => {
					const s = statsMap[p.x] ?? {};
					if (isCompiled === "averages") {
						return [p.trialName, p.city, p.region, p.countryCode, p.x, s.min, s.q1, s.median, s.q3, s.max];
					} else if (isCompiled === "allReps") {
						return [
							p.trialName,
							p.city,
							p.region,
							p.countryCode,
							p.plotName,
							p.x,
							p.rep,
							s.min,
							s.q1,
							s.median,
							s.q3,
							s.max,
						];
					} else {
						return [p.plotName, p.x, p.rep, s.min, s.q1, s.median, s.q3, s.max];
					}
				}),
				hovertemplate,
				marker: {
					color: marker.color,
					symbol: marker.shape,
					size: 8,
					line: { color: "black", width: 0.5 },
				},
				showlegend: !disableColors,
			});
		}

		const layout = {
			xaxis: {
				title: { text: "Treatment", standoff: 5 },
				type: "category",
				tickangle: 0,
				automargin: true,
				range: [-0.3, boxX.length - 0.7],
			},
			yaxis: { title: { text: axisYTitle, standoff: 10 }, zeroline: false, automargin: true },
			legend: {
				orientation: "h",
				title: { text: isCompiled ? "Cooperators" : "Replicates" },
				x: 1,
				xanchor: "right",
				y: -0.2,
				yanchor: "top",
			},
			margin: { l: axisYTitle ? 60 : 6, r: 20, b: 80, t: marginTop, pad: 0 },
			hovermode: "closest",
			autosize: true,
			annotations: [...letterAnnotations],
			boxmode: "overlay",
		};

		setChartLoading(false);
		setChartOptions({ data: traces, layout });
	}

	function medianBy(array) {
		array.sort((a, b) => b - a);
		const length = array.length;
		if (length % 2 == 0) {
			return (array[length / 2] + array[length / 2 - 1]) / 2;
		} else {
			return array[Math.floor(length / 2)];
		}
	}

	function getValuesAboveOrBelowMedian(values, median, aboveMedian) {
		if (!values || values.length === 0) return 0;

		values.sort(function (a, b) {
			return a - b;
		});

		var half = Math.floor(values.length / 2);

		if (values.length % 2) {
			return aboveMedian ? values.slice(half + 1, values.length) : values.slice(0, half);
		}

		let allValuesAreTheSame = _.every(values, (v) => {
			return Number(v).toFixed(5) === Number(median).toFixed(5);
		});
		if (allValuesAreTheSame) {
			return [median];
		}

		return aboveMedian ? _.filter(values, (value) => value > median) : _.filter(values, (value) => value < median);
	}

	return (
		<Segment
			basic
			loading={chartLoading}
			style={{ borderRadius: "8px", border: "1px solid var(--border)", background: "#ffffffe6" }}
		>
			<AFPlot
				data={chartOptions.data}
				layout={chartOptions.layout}
				config={{
					toImageButtonOptions: {
						filename: snapshotFileName,
					},
				}}
				useResizeHandler
				style={{ width: "100%", height: `${chartHeight ?? 400}px` }}
			/>
		</Segment>
	);
};

ProtocolBoxWhiskerChart.propTypes = {
	data: PropTypes.array,
	tukeyData: PropTypes.array,
	tukeyRunning: PropTypes.bool,
	assessmentName: PropTypes.string,
	trialName: PropTypes.string,
	axisYTitle: PropTypes.string,
	chartHeight: PropTypes.number,
	allTreatmentData: PropTypes.array,
	needToRerunStats: PropTypes.bool,
	needToRerunNormalization: PropTypes.bool,
	initialGroupTrialDataCheckedCount: PropTypes.array,
	allTreatmentsUnchecked: PropTypes.bool,
	hideUncheckedTrialData: PropTypes.bool,
	isCompiled: PropTypes.oneOf(["averages", "allReps", undefined]),
	disableColors: PropTypes.bool,
	hideMeanComparisonResults: PropTypes.bool,
	chartType: PropTypes.string,
};

export default ProtocolBoxWhiskerChart;
